import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import type { Activity, DashboardScope } from "@/api/queries";
import { useDashboard } from "@/api/queries";
import { useMe } from "@/auth/useMe";
import { NuevoSeguimiento } from "@/actividades/NuevoSeguimiento";
import { DetalleEvento } from "@/agenda/DetalleEvento";
import { CHANNEL_BAR, channelOf, isScheduled } from "@/agenda/model";
import {
  Button,
  EmptyState,
  ErrorState,
  Page,
  Reserved,
  Seg,
  Select,
  Spinner,
  Tag,
} from "@/components/ui";
import { NuevaEmpresa } from "@/empresas/NuevaEmpresa";

/**
 * Screen 01 of the Modernist design — the landing screen (.docs/08 §7 F5).
 *
 * Built last because it links to everything else, and built **whole**: three of
 * its four KPI cells carry real figures, and the fourth — plus the progress bar
 * and the seller ranking — are reserved holes (§5, D22). They keep their exact
 * space rather than being cut or filled with zeros, so connecting a source of
 * money later is filling a hole that already has its dimensions.
 *
 * One request, not six. This is the screen every session opens on, and
 * resolving it client-side would make it the slowest in the app.
 */

const TIME = new Intl.DateTimeFormat("es-MX", { hour: "2-digit", minute: "2-digit" });
const LONG_DAY = new Intl.DateTimeFormat("es-MX", { weekday: "long", day: "numeric" });
const MONTH_LABEL = new Intl.DateTimeFormat("es-MX", {
  month: "long",
  year: "numeric",
});

/** `YYYY-MM` for the last twelve months, newest first. */
function recentMonths(count = 12): string[] {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${date.getFullYear()}-${month}`;
  });
}

function labelForMonth(value: string): string {
  const [year, month] = value.split("-");
  return MONTH_LABEL.format(new Date(Number(year), Number(month) - 1, 1));
}

export function Inicio() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const [openEvent, setOpenEvent] = useState<Activity | null>(null);
  const [creating, setCreating] = useState(false);
  const [logging, setLogging] = useState(false);

  const isManager = me?.is_manager ?? false;
  const months = recentMonths();
  const month = params.get("mes") ?? months[0]!;
  // `mine` unless a manager has asked for the team. The server honours `team`
  // for managers only, so this is a view preference and not a privilege claim.
  const scope: DashboardScope =
    isManager && params.get("vista") === "equipo" ? "team" : "mine";

  const dashboard = useDashboard(scope, month);

  function update(changes: Record<string, string>) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    });
  }

  const data = dashboard.data;
  // The day's panel is the agenda's, and the agenda holds dated work only.
  const today = (data?.today ?? []).filter(isScheduled);
  const firstName = (me?.name || me?.email || "").split(/[\s@]/)[0] ?? "";

  return (
    <Page
      title={t("home.greeting", { name: firstName })}
      subtitle={
        data
          ? t("home.period", {
              month: labelForMonth(data.month),
              day: data.day_of_month,
              days: data.days_in_month,
            })
          : undefined
      }
      actions={
        <>
          {isManager ? (
            <div className="flex items-center gap-2">
              <Seg<DashboardScope>
                name="home-scope"
                value={scope}
                onChange={(value) =>
                  update({ vista: value === "team" ? "equipo" : "" })
                }
                options={[
                  { value: "mine", label: t("home.mine") },
                  { value: "team", label: t("home.team") },
                ]}
              />
              <Tag
                tone="outline"
                className="text-[10px] tracking-[0.08em] uppercase"
                title={t("home.managerOnly")}
              >
                {t("home.manager")}
              </Tag>
            </div>
          ) : null}
          <Select
            value={month}
            aria-label={t("home.month")}
            className="w-auto"
            onChange={(event) => update({ mes: event.target.value })}
          >
            {months.map((value) => (
              <option key={value} value={value}>
                {labelForMonth(value)}
              </option>
            ))}
          </Select>
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t("home.newProspect")}
          </Button>
        </>
      }
    >
      {dashboard.isPending ? (
        <Spinner label={t("home.loading")} />
      ) : dashboard.isError || data === undefined ? (
        <ErrorState
          message={t("home.loadFailed")}
          onRetry={() => void dashboard.refetch()}
        />
      ) : (
        <>
          <div className="grid border-y-2 border-rule sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              label={t("home.kpiProspects")}
              value={data.prospects.total}
              chips={
                <>
                  <Tag tone="accent">
                    {t("home.newThisMonth", { count: data.prospects.new_this_month })}
                  </Tag>
                  <span className="text-mute-600">
                    {t("home.interested", { count: data.prospects.interested })}
                  </span>
                </>
              }
            />
            <Kpi
              label={t("home.kpiEvents")}
              value={data.events_today.total}
              chips={
                <>
                  <Tag>
                    {t("home.byChannel", {
                      visits: data.events_today.visits,
                      calls: data.events_today.calls,
                    })}
                  </Tag>
                  <span className="text-mute-600">
                    {t("home.messages", { count: data.events_today.messages })}
                  </span>
                </>
              }
            />
            <Kpi
              label={t("home.kpiFollowUps")}
              value={data.follow_ups_month.total}
              chips={
                <>
                  <Tag tone="accent">
                    {t("home.completed", { count: data.follow_ups_month.completed })}
                  </Tag>
                  <span className="text-mute-600">
                    {t("home.stillPending", {
                      count: data.follow_ups_month.pending,
                    })}
                  </span>
                </>
              }
            />
            {/* The fourth cell is red and full-bleed in the design, and there
                is nothing to put in it: no sale, target or contract model
                exists (D22). It keeps the height of the three beside it so the
                band does not jump on the day it is connected. */}
            <div className="border-t border-rule p-5 sm:border-t-0 sm:border-l">
              <Reserved label={t("home.salesTitle")} className="h-full">
                {t("home.salesReserved")}
              </Reserved>
            </div>
          </div>

          <div className="grid gap-8 pt-7 lg:grid-cols-[1.15fr_1fr]">
            <section className="lg:border-r-2 lg:border-rule lg:pr-8">
              <h2 className="mb-4 text-[19px]">{t("home.progressTitle")}</h2>
              <Reserved label={t("home.progressTitle")} className="mb-6 min-h-[132px]">
                {t("home.progressReserved")}
              </Reserved>

              <h2 className="mb-4 text-[19px]">{t("home.rankingTitle")}</h2>
              <Reserved label={t("home.rankingTitle")} className="min-h-[180px]">
                {t("home.rankingReserved")}
              </Reserved>
            </section>

            <section>
              <header className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
                <h2 className="text-[19px]">
                  {t("home.todayTitle", { date: LONG_DAY.format(new Date()) })}
                </h2>
                <Link to="/agenda" className="text-[13px] no-underline hover:underline">
                  {t("home.seeAgenda")}
                </Link>
              </header>

              {today.length === 0 ? (
                <EmptyState>{t("home.noEventsToday")}</EmptyState>
              ) : (
                <div className="border-b border-rule">
                  {today.map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => setOpenEvent(event)}
                      className="grid w-full grid-cols-[56px_1fr_auto] items-center gap-3.5 border-t border-rule px-2 py-3.5 text-left hover:bg-mute-200"
                    >
                      <span className="font-display text-sm font-extrabold tabular-nums">
                        {TIME.format(new Date(event.due_at))}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">
                          {event.company_name ?? t("agenda.noCompany")}
                        </span>
                        <span className="block truncate text-xs text-mute-600">
                          {event.comment || event.kind_display}
                        </span>
                      </span>
                      {/* The channel bar, same colours and same integers as the
                          agenda's — `agenda/model.ts` owns the mapping so the
                          two screens cannot drift (D24). */}
                      <span
                        aria-hidden="true"
                        className="h-6 w-1"
                        style={{ background: CHANNEL_BAR[channelOf(event.kind)] }}
                      />
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                <Button onClick={() => setLogging(true)}>
                  {t("home.logFollowUp")}
                </Button>
                <Link to="/agenda" className="btn btn-secondary">
                  {t("home.scheduleVisit")}
                </Link>
              </div>
            </section>
          </div>
        </>
      )}

      <DetalleEvento event={openEvent} onClose={() => setOpenEvent(null)} />
      {creating ? <NuevaEmpresa open onClose={() => setCreating(false)} /> : null}
      {logging ? <NuevoSeguimiento open onClose={() => setLogging(false)} /> : null}
    </Page>
  );
}

function Kpi({
  label,
  value,
  chips,
}: {
  label: string;
  value: number;
  chips: ReactNode;
}) {
  return (
    <div className="border-t border-rule p-5 first:border-t-0 sm:border-t-0 sm:border-l sm:first:border-l-0">
      <p className="kicker mb-3.5 text-mute-600">{label}</p>
      <p className="font-display text-[48px] leading-[0.95] font-extrabold tracking-[-0.03em] lg:text-[64px]">
        {value.toLocaleString("es-MX")}
      </p>
      <div className="mt-3.5 flex flex-wrap items-center gap-2 text-xs">{chips}</div>
    </div>
  );
}
